/**
 * Building blocks of the Pro study wizard (Z-124, rewritten 2026-10-08).
 *
 * One idea, borrowed from Apple's settings and forms: a step is a column of
 * groups. A group has its name and one line of why above it, and a white card
 * below holding the controls, stacked — label, control, help — never squeezed
 * side by side. Nothing wraps into a neighbour, so nothing jumps when a
 * number grows. FaceWise's one-screen setup still uses StepLayout and
 * WizardTextField; nothing here is shared with it.
 */
import { Box, InputAdornment, Switch, TextField, Typography, type TextFieldProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { ReactNode } from 'react'
import { LINE, LINE_SOFT } from './deskStyles'

/** Page title + one-line lead of a wizard step. */
export function StepHeading({ title, lead }: { title: string; lead: string }) {
  const c = useTheme().affexy.colors
  return (
    <Box sx={{ mb: 4 }}>
      <Typography component="h1" sx={{ fontSize: { xs: 26, md: 30 }, fontWeight: 800, letterSpacing: '-.022em', lineHeight: 1.1 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 15, color: c.ink56, mt: 1, lineHeight: 1.5 }}>{lead}</Typography>
    </Box>
  )
}

/**
 * A titled group: name and description above, the card below. `action` sits
 * at the right of the title (an "Add" button). `flush` drops the card's
 * padding for lists whose rows bring their own.
 */
export function Group({
  title,
  description,
  badge,
  action,
  flush,
  children,
}: {
  title: string
  description?: string
  badge?: ReactNode
  action?: ReactNode
  flush?: boolean
  children: ReactNode
}) {
  const c = useTheme().affexy.colors
  return (
    <Box component="section" sx={{ 'section + &': { mt: 4.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 1.5, px: 0.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.005em', display: 'flex', alignItems: 'center', gap: 1 }}>
            {title}
            {badge}
          </Typography>
          {description && <Typography sx={{ fontSize: 13.5, color: c.ink56, mt: 0.375, lineHeight: 1.45 }}>{description}</Typography>}
        </Box>
        {action && <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>{action}</Box>}
      </Box>
      <Card flush={flush}>{children}</Card>
    </Box>
  )
}

export function Card({ children, flush, sx }: { children: ReactNode; flush?: boolean; sx?: object }) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '14px',
        border: `1px solid ${LINE}`,
        boxShadow: '0 1px 2px rgba(27,28,56,.04)',
        minWidth: 0,
        overflow: 'hidden',
        ...(flush ? {} : { p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2.5 }),
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

/** One row of a flush card; rows are separated by an inset hairline. */
export function Row({ children, sx }: { children: ReactNode; sx?: object }) {
  return (
    <Box
      data-row=""
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: 1.75,
        minWidth: 0,
        position: 'relative',
        '[data-row] + &::before': { content: '""', position: 'absolute', top: 0, left: { xs: 16, sm: 20 }, right: 0, borderTop: `1px solid ${LINE_SOFT}` },
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

/** Settings-style row: what it is on the left, a switch on the right. */
export function SwitchRow({
  checked,
  onChange,
  title,
  sub,
  disabled,
  children,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  title: string
  sub?: string
  disabled?: boolean
  children?: ReactNode
}) {
  const c = useTheme().affexy.colors
  return (
    <Row>
      <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{title}</Typography>
          {sub && <Typography sx={{ fontSize: 13, color: c.ink56, lineHeight: 1.45, mt: 0.25 }}>{sub}</Typography>}
        </Box>
        <Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} sx={{ flexShrink: 0 }} />
      </Box>
      {children && <Box sx={{ mt: 1.75 }}>{children}</Box>}
    </Row>
  )
}

/** Label above a control, optional marker after it, help or error below. */
export function Field({
  label,
  optional,
  help,
  error,
  children,
}: {
  label?: string
  optional?: string
  help?: ReactNode
  error?: string
  children: ReactNode
}) {
  const c = useTheme().affexy.colors
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875, minWidth: 0 }}>
      {label && (
        <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 600, color: c.ink }}>
          {label}
          {optional && (
            <Box component="span" sx={{ fontWeight: 400, color: c.ink48 }}>
              {` · ${optional}`}
            </Box>
          )}
        </Typography>
      )}
      {children}
      {error ? (
        <Typography component="span" sx={{ fontSize: 13, color: c.danger }}>
          {error}
        </Typography>
      ) : (
        help && (
          <Typography component="span" sx={{ fontSize: 13, color: c.ink56, lineHeight: 1.45 }}>
            {help}
          </Typography>
        )
      )}
    </Box>
  )
}

type SxObject = Record<string, unknown>

/** Caller's sx over the base one, one level deep — so overriding a nested
 *  selector adds to it instead of replacing the whole block. */
function mergeSx(base: SxObject, extra: unknown): SxObject {
  if (!extra || typeof extra !== 'object' || Array.isArray(extra)) return base
  const out: SxObject = { ...base }
  for (const [k, v] of Object.entries(extra as SxObject)) {
    const b = out[k]
    out[k] = b && v && typeof b === 'object' && typeof v === 'object' ? { ...(b as SxObject), ...(v as SxObject) } : v
  }
  return out
}

type DeskInputProps = Omit<TextFieldProps, 'variant' | 'prefix'> & { suffix?: ReactNode; prefix?: ReactNode }

/** 42px text input, 10px corners, a soft coral focus ring. */
export function DeskInput({ suffix, prefix, multiline, slotProps, sx, ...rest }: DeskInputProps) {
  const c = useTheme().affexy.colors
  return (
    <TextField
      variant="outlined"
      fullWidth
      multiline={multiline}
      slotProps={{
        ...slotProps,
        input: {
          startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined,
          endAdornment: suffix ? (
            <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 14, color: c.ink56 } }}>
              {suffix}
            </InputAdornment>
          ) : undefined,
          ...(slotProps?.input as object),
        },
      }}
      sx={mergeSx(
        {
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff',
            borderRadius: '10px',
            fontSize: 15,
            transition: 'box-shadow .15s ease',
            ...(multiline ? { py: 1.375, px: 1.75 } : { height: 42 }),
            '& fieldset': { borderColor: LINE, transition: 'border-color .15s ease' },
            '&:hover fieldset': { borderColor: c.ink32 },
            '&.Mui-focused fieldset': { borderColor: c.coral, borderWidth: 1 },
            '&.Mui-focused': { boxShadow: `0 0 0 4px ${c.coralSoft}` },
          },
          '& input': { fontVariantNumeric: 'tabular-nums', px: 1.75 },
          '& .MuiInputBase-inputAdornedStart': { pl: 0 },
          '& .MuiInputBase-inputAdornedEnd': { pr: 0 },
          // A number input's spinner arrows are a phone habit; the field takes typing.
          '& input[type=number]': { MozAppearance: 'textfield' },
          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        },
        sx,
      )}
      {...rest}
    />
  )
}

/** Apple-style segmented control: a grey track, the chosen segment a white pill. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  fullWidth,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
  label: string
  fullWidth?: boolean
}) {
  const c = useTheme().affexy.colors
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{ display: fullWidth ? 'flex' : 'inline-flex', p: '3px', gap: '2px', bgcolor: '#EEEEF3', borderRadius: '10px', maxWidth: '100%' }}
    >
      {options.map((o) => {
        const on = o.value === value
        return (
          <Box
            key={o.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            sx={{
              flex: fullWidth ? 1 : undefined,
              font: 'inherit',
              fontSize: 13.5,
              px: 1.75,
              height: 32,
              border: 0,
              borderRadius: '8px',
              cursor: 'pointer',
              bgcolor: on ? '#fff' : 'transparent',
              boxShadow: on ? '0 1px 3px rgba(27,28,56,.12), 0 0 0 .5px rgba(27,28,56,.06)' : 'none',
              color: on ? c.ink : c.ink72,
              fontWeight: on ? 700 : 500,
              whiteSpace: 'nowrap',
              transition: 'background-color .18s ease, box-shadow .18s ease, color .18s ease',
            }}
          >
            {o.label}
          </Box>
        )
      })}
    </Box>
  )
}

export function Tag({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'ok' | 'warn' | 'coral' | 'danger' }) {
  const c = useTheme().affexy.colors
  const palette = {
    neutral: { bg: '#F0F0F4', fg: c.ink72 },
    ok: { bg: '#E6F4EE', fg: '#1F8A5B' },
    warn: { bg: '#FFF4D6', fg: '#8A6000' },
    coral: { bg: c.coralSoft, fg: c.coralHover },
    danger: { bg: '#FBE9E8', fg: c.danger },
  }[tone]
  return (
    <Box
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '20px',
        px: 1,
        borderRadius: 99,
        bgcolor: palette.bg,
        color: palette.fg,
        whiteSpace: 'nowrap',
        display: 'inline-block',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {children}
    </Box>
  )
}

export function Kbd({ children }: { children: ReactNode }) {
  const c = useTheme().affexy.colors
  return (
    <Box
      component="kbd"
      sx={{
        fontFamily: 'inherit',
        fontSize: 11,
        lineHeight: '16px',
        color: c.ink56,
        border: `1px solid ${LINE}`,
        borderBottomWidth: 2,
        borderRadius: '4px',
        px: 0.625,
        bgcolor: '#fff',
        minWidth: 18,
        textAlign: 'center',
        display: 'inline-block',
      }}
    >
      {children}
    </Box>
  )
}
